import { observer } from 'mobx-react';
import { Fragment, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Page, Pagebar, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { BottomBar, BottomBarActions } from '@monorepo/base/src/components/bottom-bar/bottom-bar';
import { PrimaryButton } from '@monorepo/base/src/components/buttons/buttons';
import { Disable } from '@monorepo/base/src/components/disable/disable';
import { Input } from '@monorepo/base/src/components/form/input/input';
import { Toggle } from '@monorepo/base/src/components/form/toggle/toggle';
import { Nav, NavItem, NavPage } from '@monorepo/base/src/components/nav/nav';
import { Spacer } from '@monorepo/base/src/components/spacer/spacer';
import { BidderApi } from '../../../../modules/apis/bidder.api';
import routes from '../../../../modules/routes/index.routes';
import { useNavigateParams } from '@monorepo/tools/src/lib/hooks/tools/use-navigate-params';
import { DispatchLogsTypes, useLogs } from '@monorepo/controlled/src/hooks/use-logs';
import { BidderModelTableModel, IBidderModelApi } from '../../../../modules/models/bidder-model.model';
import { ErrorMsg } from '../../../../modules/components/error-msg/error-msg';
import { Decisions } from '../../../../modules/enums/decisions';
import { Statuses } from '../../../../modules/enums/status';
import { sentenceCase } from 'change-case';

function patchModel(prev: BidderModelTableModel, patch: Partial<IBidderModelApi>): BidderModelTableModel {
	return new BidderModelTableModel({ ...prev.toApi(), ...patch });
}

export const EditBidderModel = observer(() => {
	const { modelName: modelNameParam } = useParams() as { modelName: string };
	const decodedName = modelNameParam ? decodeURIComponent(modelNameParam) : '';
	const navigate = useNavigateParams();
	const { dispatchLog } = useLogs();

	const [model, setModel] = useState<BidderModelTableModel | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		setIsLoading(true);
		setError(null);

		BidderApi.getModels()
			.then(res => {
				if (cancelled) {
					return;
				}
				const row = res.models?.find((m: IBidderModelApi) => m.model_name === decodedName);
				if (!row) {
					setError('Model not found');
					setModel(null);
				} else {
					setModel(new BidderModelTableModel(row));
				}
			})
			.catch(e => {
				if (!cancelled) {
					setError(e instanceof Error ? e.message : String(e));
				}
			})
			.finally(() => {
				if (!cancelled) {
					setIsLoading(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [decodedName]);

	const onSave = async () => {
		if (!model) {
			return;
		}
		setIsSaving(true);
		setError(null);
		try {
			await BidderApi.updateModel({ update_model: model.toUpdatePayload() });
			dispatchLog({
				msg: `Model ${model.modelName} was saved`,
				type: DispatchLogsTypes.Success,
			});
			navigate(routes.bidder.models());
		} catch (e: unknown) {
			setError(e instanceof Error ? e.message : String(e));
			dispatchLog({
				msg: 'Failed to save model',
				type: DispatchLogsTypes.Error,
			});
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<NavPage>
			<Nav>
				<NavItem to={routes.bidder.models()}>Models</NavItem>
			</Nav>
			<Disable is={isLoading}>
				<Page>
					<Pagebar>
						<PagebarTitle>Edit model{model ? `: ${model.modelName}` : ''}</PagebarTitle>
					</Pagebar>

					{model ? (
						<Fragment>
							<Input
								topLabel="Model name"
								value={model.modelName}
								onValue={v => setModel(m => (m ? patchModel(m, { model_name: v }) : null))}
								disabled
							/>
							<Spacer />
							<Input
								topLabel="Campaigns"
								value={model.campaigns}
								onValue={v => setModel(m => (m ? patchModel(m, { campaigns: v }) : null))}
								disabled
							/>
							<Spacer />
							<Input topLabel="Last successful build" value={model.lastSuccessfulBuild} disabled />
							<Spacer />
							<Input
								type="number"
								topLabel="Real advertiser ID"
								value={model.realAdvertiserId.toString()}
								onValue={v => setModel(m => (m ? patchModel(m, { real_advertiser_id: parseInt(v, 10) || 0 }) : null))}
							/>
							<Spacer />
							<div style={{ marginLeft: 100, marginTop: 10 }}>
								<Toggle
									key={model.shouldBuild ? 'y' : 'n'}
									align="left"
									title="Should build"
									label={model.shouldBuild ? Decisions.Yes : Decisions.No}
									onChange={should_build => setModel(m => (m ? patchModel(m, { should_build }) : null))}
									defaultValue={model.shouldBuild}
								/>
								<Spacer />
								<Toggle
									key={model.status}
									align="left"
									title="Status"
									label={
										model.status?.toUpperCase() === 'ACTIVE'
											? sentenceCase(Statuses.Active)
											: sentenceCase(Statuses.Paused)
									}
									onChange={active =>
										setModel(m => (m ? patchModel(m, { status: active ? Statuses.Active : Statuses.Paused }) : null))
									}
									defaultValue={model.status?.toUpperCase() === 'ACTIVE'}
								/>
							</div>
						</Fragment>
					) : null}
				</Page>
			</Disable>
			<BottomBar>
				{error ? <ErrorMsg error={error} /> : null}
				<BottomBarActions>
					<PrimaryButton
						disabled={isLoading || !model || isSaving}
						onClick={onSave}
						isRotateIcon={isSaving}
						icon={isSaving ? 'loading-01' : 'send-03'}>
						Save
					</PrimaryButton>
				</BottomBarActions>
			</BottomBar>
		</NavPage>
	);
});
